import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../../store/slices/authentication";
import {
  useGetScrapbookPostsQuery,
  useGetAdminStatusQuery,
} from "../../../store/slices/api";
import BlockedFeatureBanner from "../../shared/BlockedFeatureBanner";
import SharedHorizontalTabs from "../../shared/HorizontalTabs";
import CreatePost from "./CreatePost";
import PostCard from "./PostCard";
import SampleScrapbook, { SampleNotice } from "./SampleScrapbook";
import DayBreak, { dayKey } from "./DayBreak";
import { CATEGORIES } from "./categories";

// The Scrapbook — the family photo wall. Asked for at the 2026-07-09 meeting as
// section A, alongside The Family Footprint (section B).
//
// Named on 2026-09-09. It was "Our Gallery" first, and that was wrong: a gallery
// is somewhere you walk through and look, which quietly tells people the wall is
// not theirs to add to. A scrapbook is the opposite — it is defined by being
// added to, it holds photographs and clippings and mementos alike (which is
// exactly what this accepts), and unlike "Family Album" it cannot be confused
// with the Family Tree or the member directory.
//
// The Bulletin is where the family talks; this is where it looks at pictures.
// The meeting was explicit about why: "our bulletin is turning into a family
// Facebook wall, so let's make that happen in the Our Gallery section instead."
// (That is the meeting's wording; the section was renamed The Scrapbook later.)
// Everything here stays deliberately light — a caption, five reactions and short
// comments — because the moment it grows threads and titles it becomes a second
// Bulletin and the split stops being worth anything.

// "Sample" is its own tab, first in the row (Sam, 2026-09-11): a clearly marked
// example of an active Scrapbook, with no way to post from it. The page opens on
// it while the wall has no real posts, and on Everything once it does. It stays
// until Sam says to pull it.
const SAMPLE = "sample";
const EVERYTHING = "everything";
const TABS = [
  { key: SAMPLE, label: "Sample" },
  { key: EVERYTHING, label: "Everything" },
  ...CATEGORIES,
];

const LIMIT = 24;

// One page of the wall, fetched on its own.
//
// "Show more" used to swap the request's offset, which REPLACED the 24 posts on
// screen with the next 24 -- the first page vanished the moment you asked for
// more. Each page now keeps its own request, so pages stack, and anything that
// refreshes the wall (a new post, a reaction, a removal) refreshes every page
// that is showing.
function WallPage({ category, offset, viewerId, isAdmin }) {
  const { data } = useGetScrapbookPostsQuery({ category, limit: LIMIT, offset });
  // The page above, only to read its last post's date so a day that runs across
  // the page boundary is not announced twice. Same arguments as that page's own
  // request, so this is answered from the cache and sends nothing.
  const { data: above } = useGetScrapbookPostsQuery(
    { category, limit: LIMIT, offset: Math.max(offset - LIMIT, 0) },
    { skip: offset === 0 }
  );
  const posts = data?.posts || [];
  const lastAbove = above?.posts?.[above.posts.length - 1];

  return posts.map((p, i) => {
    const before = i === 0 ? lastAbove : posts[i - 1];
    const opensDay = !before || dayKey(p.created_at) !== dayKey(before.created_at);
    return (
      <React.Fragment key={p.id}>
        {opensDay && <DayBreak when={p.created_at} />}
        <PostCard post={p} viewerId={viewerId} isAdmin={isAdmin} index={offset + i} />
      </React.Fragment>
    );
  });
}

// `embedded`: shown as a tab inside Family Connect (Sam, 2026-09-26). Family
// Connect already owns the "tab" address setting, so the wall's own tabs use
// "wall" there, and the page title is left to Family Connect.
function Scrapbook({ embedded = false }) {
  const param = embedded ? "wall" : "tab";
  const user = useSelector(selectUser);
  const individualId = user?.externalId ? parseInt(user.externalId) : null;
  const [searchParams, setSearchParams] = useSearchParams();
  // Which tab opens when the address names none: Sample while the wall has no
  // real post, Everything once it has one. Decided here rather than by the shared
  // tab hook, which always falls back to the FIRST tab -- that would open Sample
  // forever, and bounce a click on Everything straight back to Sample.
  const { data: anyPosts, isError: anyPostsError } = useGetScrapbookPostsQuery({
    category: "",
    limit: 1,
    offset: 0,
  });
  const wallHasPosts = (anyPosts?.total || 0) > 0;
  const asked = TABS.findIndex((t) => t.key === searchParams.get(param));
  const deciding = asked === -1 && anyPosts === undefined && !anyPostsError;
  const currentTabIndex =
    asked !== -1 ? asked : TABS.findIndex((t) => t.key === (wallHasPosts ? EVERYTHING : SAMPLE));
  const tabKey = TABS[currentTabIndex].key;
  const isSample = tabKey === SAMPLE;
  const category = isSample || tabKey === EVERYTHING ? "" : tabKey;

  const [pages, setPages] = useState(1);

  // The first page decides loading, error and empty. The last page decides
  // whether "Show more" is still busy. Both requests are shared with the
  // WallPage that shows them, so nothing is fetched twice.
  const {
    data,
    isLoading,
    isFetching: firstPageFetching,
    isError,
    refetch,
  } = useGetScrapbookPostsQuery(
    { category, limit: LIMIT, offset: 0 },
    { skip: isSample }
  );
  // While "Try again" runs, RTK Query reports neither loading nor error and no
  // data; that must read as loading, not "Nothing here yet".
  const firstPageLoading = isLoading || (data === undefined && firstPageFetching);
  const { isFetching } = useGetScrapbookPostsQuery(
    { category, limit: LIMIT, offset: (pages - 1) * LIMIT },
    { skip: isSample }
  );
  const { data: adminData } = useGetAdminStatusQuery(individualId, {
    skip: !individualId,
  });
  const isAdmin = adminData?.is_admin || false;

  const handleTabChange = (index) => {
    const next = new URLSearchParams(searchParams);
    next.set(param, TABS[index].key);
    setSearchParams(next, { replace: true });
    setPages(1);
  };

  const posts = data?.posts || [];
  const total = data?.total || 0;
  const hasMore = pages * LIMIT < total;

  return (
    <BlockedFeatureBanner
      featureKey="is_scrapbook_blocked"
      featureName="The Scrapbook"
      individualId={individualId}
      mode="full"
    >
      <div className={embedded ? "w-full" : "h-full w-full overflow-y-auto bg-dupontCream dark:bg-neutral-900"}>
        {/* Wide enough for a 620px column of photographs PLUS a margin either
              side. Two 168px gutters and their gaps cost 380px, so anything
              narrower squeezes the card until the bylines wrap. */}
          <div className="max-w-[1030px] mx-auto px-[18px] pt-[22px] pb-[40px]">
          <div className="text-center mb-[18px] max-w-[660px] mx-auto">
            {!embedded && (
              <h1 className="font-display font-semibold text-[30px] sm:text-[34px] leading-[1.1]
                             tracking-[-0.015em] text-dupontBlue dark:text-darkText">
                The Scrapbook
              </h1>
            )}
            <p className="font-body italic text-[17px] text-gray-500 dark:text-gray-400 mt-[7px]">
              Photographs, clips and keepsakes — add yours
            </p>
            <div className="mx-auto mt-[11px] h-[3px] w-[46px] bg-dupontGold" aria-hidden="true" />
          </div>

          <div className="mb-[20px] max-w-[660px] mx-auto">
            <SharedHorizontalTabs.Component
              tabs={TABS.map((t) => t.label)}
              initialIndex={deciding ? -1 : currentTabIndex}
              onChange={handleTabChange}
              className="justify-center flex-wrap gap-x-[20px] border-b border-dupontGold/30 pb-[8px]"
            />
          </div>

          {/* No posting from the Sample tab -- its notice sends people to the others. */}
          {!isSample && (
            <div className="max-w-[660px] mx-auto"><CreatePost onPosted={() => setPages(1)} /></div>
          )}

          {deciding ? (
            <p className="text-center text-gray-500 py-[36px]">Loading…</p>
          ) : isSample ? (
            <>
              <SampleNotice
                tabs={TABS.filter((t) => t.key !== SAMPLE)}
                onPick={(key) => handleTabChange(TABS.findIndex((t) => t.key === key))}
              />
              <SampleScrapbook viewerId={individualId} />
            </>
          ) : firstPageLoading ? (
            <p className="text-center text-gray-500 py-[36px]">Loading…</p>
          ) : isError ? (
            <div className="text-center py-[36px]">
              <p className="text-gray-500 dark:text-gray-400">
                Couldn’t load the scrapbook right now.
              </p>
              <button
                type="button"
                onClick={refetch}
                className="mt-[10px] px-[18px] py-[8px] rounded-[6px] text-[14px] font-semibold
                           border border-dupontGoldLt/50 text-dupontBlue dark:text-darkText"
              >
                Try again
              </button>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-[52px] text-gray-500 dark:text-gray-400">
              <p className="text-[17px]">Nothing here yet.</p>
              <p className="text-[14px] mt-[6px]">
                Be the first — add a photo above.
              </p>
            </div>
          ) : (
            <>
              {Array.from({ length: pages }, (_, n) => (
                <WallPage
                  key={n}
                  category={category}
                  offset={n * LIMIT}
                  viewerId={individualId}
                  isAdmin={isAdmin}
                />
              ))}
              {hasMore && (
                <div className="text-center mt-[6px]">
                  <button
                    type="button"
                    disabled={isFetching}
                    onClick={() => setPages((n) => n + 1)}
                    className="px-[18px] py-[8px] rounded-[6px] text-[14px] font-semibold
                               border border-dupontGoldLt/50 text-dupontBlue dark:text-darkText
                               disabled:opacity-40"
                  >
                    {isFetching ? "Loading…" : "Show more"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </BlockedFeatureBanner>
  );
}

export default Scrapbook;
